import { FlexWrapper, Input, notify, Switch } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { Avatar } from '@affine/component/ui/avatar';
import { Button } from '@affine/component/ui/button';
import { authAtom } from '@affine/core/components/atoms/auth';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useCatchEventCallback } from '@affine/core/components/hooks/use-catch-event-hook';
import {
  useMarketingConsent,
  useUpdateMarketingConsent,
} from '@affine/core/modules/marketing-consent';
import {
  useGetUserAttribute,
  useUserAttributes,
} from '@affine/core/modules/user-attributes';
// DNB_FIX добавить поддержку AIChat
import { AiChatPanel } from '@affine/core/plugins';
import { ServerDeployment } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { CameraIcon } from '@docnboard/icons/blocksuite/rc';
import { ChevronRightIcon } from '@docnboard/icons/rc';
import {
  getMarketingControllerGetMarketingConsentsQueryKey,
  UserAttributeName,
} from '@docnboard/rest';
import { useQueryClient } from '@tanstack/react-query';
import {
  useEnsureLiveData,
  useLiveData,
  useService,
} from '@toeverything/infra';
import { useSetAtom } from 'jotai';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { parsePhoneNumber } from 'react-phone-number-input';

import {
  AuthService,
  ServerConfigService,
  SubscriptionService,
  UserQuotaService,
} from '../../../../modules/cloud';
import { openSettingModalAtom, openSignOutModalAtom } from '../../../atoms';
import { Upload } from '../../../pure/file-upload';
import { PhoneNumberModal } from './phone-number-modal';
import * as styles from './style.css';
import { UsageProgress } from './usage-progress';

export const UserAvatar = () => {
  const t = useI18n();
  const session = useService(AuthService).session;
  const account = useEnsureLiveData(session.account$);

  // DNB_FIX не даем изменять и удалять аватарку пользователя, если enabledChangeAccount=false
  const serverConfig = useService(ServerConfigService).serverConfig;
  const enabledChangeAccount = useLiveData(
    serverConfig.config$.map(c => c?.enableChangeAccount)
  );
  const canAdjustAvatar = enabledChangeAccount;

  const handleUpdateUserAvatar = useAsyncCallback(
    async (file: File) => {
      try {
        track.$.settingsPanel.accountSettings.uploadAvatar();
        await session.uploadAvatar(file);
        notify.success({ title: 'Update user avatar success' });
      } catch (e) {
        // TODO(@catsjuice): i18n
        notify.error({
          title: 'Update user avatar failed',
          message: String(e),
        });
      }
    },
    [session]
  );

  const handleRemoveUserAvatar = useCatchEventCallback(async () => {
    track.$.settingsPanel.accountSettings.removeAvatar();
    await session.removeAvatar();
  }, [session]);

  return (
    <Upload
      accept="image/gif,image/jpeg,image/jpg,image/png,image/svg"
      fileChange={handleUpdateUserAvatar}
      disabled={!canAdjustAvatar}
      data-testid="upload-user-avatar"
    >
      <Avatar
        size={56}
        name={account.label}
        url={account.avatar}
        hoverIcon={canAdjustAvatar ? <CameraIcon /> : undefined}
        onRemove={
          account.avatar && canAdjustAvatar ? handleRemoveUserAvatar : undefined
        }
        avatarTooltipOptions={{
          content: canAdjustAvatar ? t['Click to replace photo']() : undefined,
        }}
        removeTooltipOptions={{
          content: canAdjustAvatar ? t['Remove photo']() : undefined,
        }}
        data-testid="user-setting-avatar"
        removeButtonProps={{
          ['data-testid' as string]: 'user-setting-remove-avatar-button',
        }}
      />
    </Upload>
  );
};

export const AvatarAndName = () => {
  const t = useI18n();
  const session = useService(AuthService).session;
  const account = useEnsureLiveData(session.account$);
  const [input, setInput] = useState<string>(account.label);

  // DNB_FIX скрываем изменения пароля и электронной почты, если enabledChangeAccount=false
  const serverConfig = useService(ServerConfigService).serverConfig;
  const enabledChangeAccount = useLiveData(
    serverConfig.config$.map(c => c?.enableChangeAccount)
  );

  const allowUpdate =
    !!input && input !== account.label && enabledChangeAccount;
  const handleUpdateUserName = useAsyncCallback(async () => {
    if (account === null) {
      return;
    }
    if (!allowUpdate) {
      return;
    }

    try {
      track.$.settingsPanel.accountSettings.updateUserName();
      await session.updateLabel(input);
    } catch (e) {
      notify.error({
        title: 'Failed to update user name.',
        message: String(e),
      });
    }
  }, [account, allowUpdate, session, input]);

  return (
    <SettingRow
      name={t['com.affine.settings.profile']()}
      desc={t['com.affine.settings.profile.message']()}
      spreadCol={false}
    >
      <FlexWrapper style={{ margin: '12px 0 24px 0' }} alignItems="center">
        <UserAvatar />

        <div className={styles.profileInputWrapper}>
          <label>{t['com.affine.settings.profile.name']()}</label>
          <FlexWrapper alignItems="center">
            <Input
              defaultValue={input}
              data-testid="user-name-input"
              placeholder={t['com.affine.settings.profile.placeholder']()}
              maxLength={64}
              minLength={0}
              style={{ width: 280, height: 32 }}
              onChange={setInput}
              onEnter={handleUpdateUserName}
              disabled={!enabledChangeAccount}
            />
            {allowUpdate ? (
              <Button
                data-testid="save-user-name"
                onClick={handleUpdateUserName}
                style={{
                  marginLeft: '12px',
                }}
              >
                {t['com.affine.editCollection.save']()}
              </Button>
            ) : null}
          </FlexWrapper>
        </div>
      </FlexWrapper>
    </SettingRow>
  );
};

const StoragePanel = () => {
  const t = useI18n();

  const quota = useService(UserQuotaService).quota;

  useEffect(() => {
    quota.revalidate();
  }, [quota]);

  const color = useLiveData(quota.color$.selector(c => c?.storageQuota));
  const usedFormatted = useLiveData(
    quota.usedFormatted$.selector(u => u?.usedStorageSize)
  );
  const maxFormatted = useLiveData(
    quota.maxFormatted$.selector(m => m?.storageQuota)
  );
  const percent = useLiveData(quota.percent$.selector(p => p?.storageQuota));

  const subscription = useService(SubscriptionService).subscription;
  useEffect(() => {
    subscription.revalidate();
  }, [subscription]);

  const proSubscription = useLiveData(subscription.pro$);
  const isFreeUser = !proSubscription;
  const workspaceQuota = useLiveData(quota.quota$);

  const loading =
    proSubscription === null || percent === null || workspaceQuota === null;
  const loadError = useLiveData(quota.error$);

  const setSettingModalAtom = useSetAtom(openSettingModalAtom);
  const onUpgrade = useCallback(() => {
    setSettingModalAtom({
      open: true,
      activeTab: 'plans',
    });
  }, [setSettingModalAtom]);

  return (
    <SettingRow
      name={t['com.affine.storage.title']()}
      desc=""
      spreadCol={false}
    >
      <UsageProgress
        onUpgrade={onUpgrade}
        hasPayment={false}
        limit={`${usedFormatted}/${maxFormatted}`}
        percent={percent}
        color={color}
        loading={loading}
        error={loadError}
        isFreeUser={isFreeUser}
        title={t['com.affine.storage.used.hint']()}
      />
    </SettingRow>
  );
};

const WorkspacePanel = () => {
  const t = useI18n();

  const quota = useService(UserQuotaService).quota;

  useEffect(() => {
    quota.revalidate();
  }, [quota]);

  const color = useLiveData(quota.color$.selector(c => c?.workspaceQuota));
  const usedFormatted = useLiveData(
    quota.usedFormatted$.selector(u => u?.usedWorkspaceCount)
  );
  const maxFormatted = useLiveData(
    quota.maxFormatted$.selector(m => m?.workspaceQuota)
  );
  const percent = useLiveData(quota.percent$.selector(p => p?.workspaceQuota));

  const subscription = useService(SubscriptionService).subscription;
  useEffect(() => {
    // revalidate subscription to get the latest status
    subscription.revalidate();
  }, [subscription]);

  const proSubscription = useLiveData(subscription.pro$);
  const isFreeUser = !proSubscription;
  const workspaceQuota = useLiveData(quota.quota$);

  const loading =
    proSubscription === null || percent === null || workspaceQuota === null;
  const loadError = useLiveData(quota.error$);

  const setSettingModalAtom = useSetAtom(openSettingModalAtom);
  const onUpgrade = useCallback(() => {
    setSettingModalAtom({
      open: true,
      activeTab: 'plans',
    });
  }, [setSettingModalAtom]);

  return (
    <SettingRow
      name={t['docnboard.workspace.title']()}
      desc=""
      spreadCol={false}
    >
      <UsageProgress
        onUpgrade={onUpgrade}
        hasPayment={false}
        limit={`${usedFormatted}/${maxFormatted}`}
        percent={percent}
        color={color}
        loading={loading}
        error={loadError}
        isFreeUser={isFreeUser}
        title={t['docnboard.workspace.used.hint']()}
      />
    </SettingRow>
  );
};

export const AccountSetting: FC = () => {
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);

  const authService = useService(AuthService);

  const t = useI18n();
  const session = authService.session;
  useEffect(() => {
    session.revalidate();
  }, [session]);
  const account = useEnsureLiveData(session.account$);
  const setAuthModal = useSetAtom(authAtom);
  const setSignOutModal = useSetAtom(openSignOutModalAtom);

  const queryClient = useQueryClient();

  const userAttributePhoneNumber = useGetUserAttribute({
    attribute: UserAttributeName.phone_number,
  });
  const currParsedPhoneNumber = parsePhoneNumber(
    (userAttributePhoneNumber.data?.data?.value as string) || ''
  );
  const { handlePhoneSave } = useUserAttributes();

  const queryMarketingConsent = useMarketingConsent();
  const queryUpdateUserSettings = useUpdateMarketingConsent({
    mutation: {
      onSuccess: async () => {
        notify.success({
          title: t['docnboard.consent.marketing.success.message'](),
          classNameTitle: styles.marketingConsentTitleNotify,
        });

        await queryClient.invalidateQueries({
          queryKey: getMarketingControllerGetMarketingConsentsQueryKey(),
        });
      },
    },
  });

  useEffect(() => {
    if (queryMarketingConsent.error) {
      notify.error({
        title: t['docnboard.consent.marketing.name'](),
        message: queryMarketingConsent.error.error?.message,
      });
    }
  }, [queryMarketingConsent.error, t]);

  // DNB_FIX скрываем изменения пароля и электронной почты, если enabledChangeAccount=false
  const serverConfig = useService(ServerConfigService).serverConfig;
  const enabledChangeAccount = useLiveData(
    serverConfig.config$.map(c => c?.enableChangeAccount)
  );
  const isSelfhosted = useLiveData(
    serverConfig.config$.selector(c => c?.type === ServerDeployment.Selfhosted)
  );

  const onChangeEmail = useCallback(() => {
    setAuthModal({
      openModal: true,
      state: 'sendEmail',
      // @ts-expect-error accont email is always defined
      email: account.email,
      emailType: account.info?.emailVerified ? 'changeEmail' : 'verifyEmail',
    });
  }, [account.email, account.info?.emailVerified, setAuthModal]);

  const onPasswordButtonClick = useCallback(() => {
    setAuthModal({
      openModal: true,
      state: 'sendEmail',
      // @ts-expect-error accont email is always defined
      email: account.email,
      emailType: account.info?.hasPassword ? 'changePassword' : 'setPassword',
    });
  }, [account.email, account.info?.hasPassword, setAuthModal]);

  const onPhoneButtonClick = useCallback(() => {
    setPhoneModalOpen(true);
  }, []);

  const onOpenSignOutModal = useCallback(() => {
    setSignOutModal(true);
  }, [setSignOutModal]);

  return (
    <>
      <SettingHeader
        title={t['com.affine.setting.account']()}
        subtitle={t['com.affine.setting.account.message']()}
        data-testid="account-title"
      />
      <SettingWrapper>
        <AvatarAndName />
        <SettingRow
          name={t['com.affine.settings.email']()}
          desc={account.email}
        >
          {enabledChangeAccount && (
            <Button onClick={onChangeEmail}>
              {account.info?.emailVerified
                ? t['com.affine.settings.email.action.change']()
                : t['com.affine.settings.email.action.verify']()}
            </Button>
          )}
        </SettingRow>
        <SettingRow
          name={t['com.affine.settings.password']()}
          desc={t['com.affine.settings.password.message']()}
        >
          {enabledChangeAccount && (
            <Button onClick={onPasswordButtonClick}>
              {account.info?.hasPassword
                ? t['com.affine.settings.password.action.change']()
                : t['com.affine.settings.password.action.set']()}
            </Button>
          )}
        </SettingRow>
        <SettingRow
          name={t['docnboard.phone-number.label']()}
          desc={
            currParsedPhoneNumber?.formatInternational() ||
            t['docnboard.setting.account.phone-number.description.default']()
          }
        >
          {enabledChangeAccount && (
            <Button onClick={onPhoneButtonClick}>
              {t['docnboard.setting.account.phone-number.button.change']()}
            </Button>
          )}
        </SettingRow>
        <StoragePanel />
        <WorkspacePanel />
        {/* DNB_FIX скрыть поддержку Copilot */}
        {/* {serverFeatures?.copilot && <AIUsagePanel />} */}
        {/* DNB_FIX добавить поддержку AIChat */}
        <AiChatPanel />
        <SettingRow
          name={t[`Sign out`]()}
          desc={t['com.affine.setting.sign.out.message']()}
          style={{ cursor: 'pointer' }}
          data-testid="sign-out-button"
          onClick={onOpenSignOutModal}
        >
          <ChevronRightIcon />
        </SettingRow>
        {/*<SettingRow*/}
        {/*  name={*/}
        {/*    <span style={{ color: 'var(--affine-warning-color)' }}>*/}
        {/*      {t['com.affine.setting.account.delete']()}*/}
        {/*    </span>*/}
        {/*  }*/}
        {/*  desc={t['com.affine.setting.account.delete.message']()}*/}
        {/*  style={{ cursor: 'pointer' }}*/}
        {/*  onClick={useCallback(() => {*/}
        {/*    toast('Function coming soon');*/}
        {/*  }, [])}*/}
        {/*  testId="delete-account-button"*/}
        {/*>*/}
        {/*  <ChevronRightIcon />*/}
        {/*</SettingRow>*/}
      </SettingWrapper>

      {!isSelfhosted && (
        <SettingRow
          name={t['docnboard.consent.marketing.name']()}
          desc={t['docnboard.consent.marketing.description']()}
        >
          {!queryMarketingConsent.isLoading &&
            !queryMarketingConsent.isError && (
              <Switch
                data-testid="marketing-consent"
                checked={queryMarketingConsent.data?.data?.subscribed}
                onChange={subscribed =>
                  queryUpdateUserSettings.mutate({
                    params: {
                      subscribed: String(subscribed),
                    },
                  })
                }
              />
            )}
        </SettingRow>
      )}
      <PhoneNumberModal
        open={phoneModalOpen}
        onOpenChange={setPhoneModalOpen}
        currentPhoneNumber={currParsedPhoneNumber?.number}
        onSave={handlePhoneSave}
      />
    </>
  );
};
